import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import b2Image from '../images/srisalimQRCode.jpg';
import { apiUrl } from '../apiUrl';

const COUNTRY_OPTIONS = ['India', 'Other'];
const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

const Donate: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    mobile: '',
    email: '',
    dateOfBirth: '',
    country: 'India',
    state: '',
    city: '',
    pinCode: '',
    address: '',
    panNo: '',
    amount: '',
    paymentMethod: '',
    consent: false,
    photo: null as File | null,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState('');
  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    // Show modal only if exactly one message is set (either success or error)
    if (successMessage || errorMessage) {
      // Prioritize success message over error message
      if (successMessage) {
        setErrorMessage(null); // Clear error message if success is set
      }
      setShowMessageModal(true);
    }
  }, [successMessage, errorMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, consent: e.target.checked }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData((prev) => ({ ...prev, photo: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset messages to ensure only one is set at a time
    setSuccessMessage(null);
    setErrorMessage(null);

    let amountValue = parseInt(formData.amount, 10);
    if (isNaN(amountValue) || amountValue < 501) {
      setErrorMessage('Minimum donation amount is ₹501. Please enter a higher amount.');
      return;
    }

    if (!formData.consent) {
      setErrorMessage('Please agree to receive updates.');
      return;
    }

    const matchedState = INDIAN_STATES.find(
      (option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase()
    );
    const selectedState = (matchedState || formData.state || '').trim();
    const hasValidState = INDIAN_STATES.some(
      (option) => option.toLowerCase() === selectedState.toLowerCase()
    );
    if (!hasValidState) {
      setErrorMessage('Please select a valid state from the dropdown list.');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('fullName', formData.fullName.trim());
    formDataToSend.append('mobile', formData.mobile.trim());
    formDataToSend.append('email', formData.email.trim());
    formDataToSend.append('dateOfBirth', formData.dateOfBirth);
    formDataToSend.append('country', formData.country.trim());
    formDataToSend.append('state', selectedState);
    formDataToSend.append('city', formData.city.trim());
    formDataToSend.append('pinCode', formData.pinCode.trim());
    formDataToSend.append('address', formData.address.trim());
    formDataToSend.append('panNo', formData.panNo.trim());
    formDataToSend.append('amount', formData.amount);
    formDataToSend.append('paymentMethod', formData.paymentMethod);
    formDataToSend.append('consent', formData.consent.toString());

    if (formData.photo) {
      formDataToSend.append('photo', formData.photo);
    }

    try {
      const res = await fetch(apiUrl('/api/donations'), {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await res.json();
      if (res.ok) {
        setValidationAttempted(false);
        setSuccessMessage('Thank you! Your donation details have been submitted. Our team will verify your payment. Submitting this form does not confirm payment.');
        setFormData({
          fullName: '',
          mobile: '',
          email: '',
          dateOfBirth: '',
          country: 'India',
          state: '',
          city: '',
          pinCode: '',
          address: '',
          panNo: '',
          amount: '',
          paymentMethod: '',
          consent: false,
          photo: null,
        });
        setStateSearchValue('');
        setShowStateOptions(false);
      } else {
        setErrorMessage(data.message || 'Error submitting donation.');
      }
    } catch (err) {
      setErrorMessage('Error submitting donation. Please try again.');
      console.error('Fetch error:', err);
    }
  };

  const handleShowQrModal = () => setShowQrModal(true);
  const handleCloseQrModal = () => setShowQrModal(false);

  const handleDownloadQr = () => {
    const link = document.createElement('a');
    link.href = b2Image;
    link.download = 'Srisailam_QR_Code.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="sf-donate-page">
      <section className="sf-donate-intro tw-py-[clamp(3rem,6vw,5rem)]">
        <Container className="sf-donate-container">
          <p className="sf-donate-eyebrow">SUPPORT OUR WORK</p>
          <h1>Support Our Mission</h1>
          <p className="sf-donate-lead tw-max-w-[56rem]">Support free education and learning opportunities for children studying up to Class 8.</p>
        </Container>
      </section>

      <section className="tw-py-[clamp(2.8rem,5vw,4.4rem)]">
        <Container className="sf-donate-container">
          <div className="sf-donate-card tw-p-5 md:tw-p-8">
            <h2>Support Our Cause</h2>
            <ol className="sf-donate-steps">
              <li>Pay manually using QR/UPI or bank transfer.</li>
              <li>Submit donation details through the form.</li>
              <li>Our team reviews the payment details for verification.</li>
            </ol>
            <p>Submitting this form does not confirm payment.</p>
            <p className="sf-donate-tax-note">
              YOUR CONTRIBUTIONS ARE ELIGIBLE FOR UPTO 50% TAX BENEFIT UNDER SECTION 80G AS SRISAILAM FOUNDATION IS REGISTERED AS NONPROFIT ORGANIZATION<br />
              PAN: ABPCS2866R | 80G NUMBER: ABPCS2866RF20251
            </p>
            <Form onSubmit={handleSubmit} onInvalidCapture={() => setValidationAttempted(true)} className={validationAttempted ? 'sf-validation-attempted' : undefined}>
              {validationAttempted && <p className="sf-validation-summary" role="alert">Please correct the highlighted fields. Your browser will explain the first error.</p>}
              <div className="tw-grid tw-gap-8 lg:tw-grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                <div className="sf-donate-payment">
                  <h4 className="details-title mb-3">Scan to Donate</h4>
                  <button type="button" className="sf-donate-qr-button" onClick={handleShowQrModal} aria-label="Enlarge donation QR code"><img
                    src={b2Image}
                    alt="QR Code for Payment"
                    className="qr-code-img mb-4"
                  /></button>
                  <h4 className="details-title mb-3">Bank Details</h4>
                  <p className="details-text">
                    <strong>Bank Name:</strong> State Bank of India<br />
                    <strong>Account Name:</strong> Srisailam Foundation<br />
                    <strong>Account Number:</strong> 20530603110<br />
                    <strong>IFSC Code:</strong> SBIN0018143<br />
                    <strong>Branch:</strong> Civil lines Raebareli
                  </p>
                  <h4 className="details-title mb-3 mt-4">UPI ID</h4>
                  <p className="details-text">
                    <strong>UPI ID:</strong> srisailamngo@sbi
                  </p>
                </div>
                <div className="sf-donate-form tw-min-w-0">
                  <h3>Your Donation Details</h3>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group controlId="formPhoto">
                        <Form.Label>
                          Upload Payment Receipt (Optional)
                        </Form.Label>
                        <Form.Control type="file" name="photo" accept="image/*" onChange={handleFileChange} />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="formFullName">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group controlId="formMobile">
                        <Form.Label>Mobile</Form.Label>
                        <Form.Control
                          type="tel"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          placeholder="Enter your mobile number"
                          pattern="[0-9]{10}"
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="formEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group controlId="formDateOfBirth">
                        <Form.Label>Date of Birth</Form.Label>
                        <Form.Control
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="formCountry">
                        <Form.Label>Country</Form.Label>
                        <Form.Select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          required
                        >
                          {COUNTRY_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group controlId="formState">
                        <Form.Label>State</Form.Label>
                        <div style={{ position: 'relative' }}>
                          <Form.Control
                            type="text"
                            value={stateSearchValue}
                            onFocus={() => setShowStateOptions(true)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setStateSearchValue(e.target.value);
                              setShowStateOptions(true);
                            }}
                            onBlur={() => {
                              const matched = INDIAN_STATES.find(
                                (option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase()
                              );
                              if (matched) {
                                setStateSearchValue(matched);
                                setFormData((prev) => ({ ...prev, state: matched }));
                              } else {
                                setStateSearchValue(formData.state || '');
                              }
                              setTimeout(() => setShowStateOptions(false), 120);
                            }}
                            placeholder="Search and select your state"
                            required
                          />
                          {showStateOptions && (
                            <div
                              className="list-group"
                              style={{
                                position: 'absolute',
                                zIndex: 20,
                                width: '100%',
                                maxHeight: '180px',
                                overflowY: 'auto',
                                border: '1px solid #dfe5ef',
                                borderRadius: '8px',
                                marginTop: '6px',
                                background: '#fff',
                              }}
                            >
                              {INDIAN_STATES.filter((option) =>
                                option.toLowerCase().includes(stateSearchValue.trim().toLowerCase())
                              )
                                .slice(0, 50)
                                .map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    className="list-group-item list-group-item-action"
                                    onMouseDown={(evt) => evt.preventDefault()}
                                    onClick={() => {
                                      setStateSearchValue(option);
                                      setFormData((prev) => ({ ...prev, state: option }));
                                      setShowStateOptions(false);
                                    }}
                                  >
                                    {option}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="formCity">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Enter your city"
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group controlId="formPinCode">
                        <Form.Label>Pin Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="pinCode"
                          value={formData.pinCode}
                          onChange={handleChange}
                          placeholder="Enter your pin code"
                          pattern="[0-9]{6}"
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="formPanNo">
                        <Form.Label>PAN No</Form.Label>
                        <Form.Control
                          type="text"
                          name="panNo"
                          value={formData.panNo}
                          onChange={handleChange}
                          placeholder="Enter your PAN number"
                          pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <Form.Group controlId="formAddress">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Enter your address"
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <Form.Group controlId="formAmount">
                        <Form.Label>Donation Amount (₹)</Form.Label>
                        <Form.Control
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleChange}
                          placeholder="Enter amount (min ₹501)"
                          min="501"
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <Form.Group controlId="formPaymentMethod">
                        <Form.Label>Payment Method</Form.Label>
                        <Form.Select
                          name="paymentMethod"
                          value={formData.paymentMethod}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select payment method</option>
                          <option value="UPI">UPI</option>
                          <option value="Credit Card">Credit Card</option>
                          <option value="Debit Card">Debit Card</option>
                          <option value="Net Banking">Net Banking</option>
                        </Form.Select>
                        <Form.Text>Record the method used for your separate payment. This form does not process card or online payments.</Form.Text>
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <Form.Group controlId="formConsent">
                        <Form.Check
                          type="checkbox"
                          label="I agree that Srisailam Foundation can reach out to me via WhatsApp/email/SMS for donation updates, campaigns, and 80G receipt."
                          name="consent"
                          checked={formData.consent}
                          onChange={handleCheckboxChange}
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-center mt-4">
                <Button variant="primary" type="submit" className="sf-donate-submit">
                  Submit Donation Details
                </Button>
              </div>
            </Form>
          </div>
        </Container>

        <Modal show={showQrModal} onHide={handleCloseQrModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>QR Code for Donation</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <img
              src={b2Image}
              alt="QR Code for Payment"
              style={{ width: '100%', maxWidth: '300px', height: 'auto', margin: '0 auto' }}
            />
            <Button variant="primary" onClick={handleDownloadQr} className="mt-3">
              Download QR Code
            </Button>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseQrModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showMessageModal} onHide={() => { setShowMessageModal(false); setSuccessMessage(null); setErrorMessage(null); }} centered>
          <Modal.Header closeButton><Modal.Title>{successMessage ? 'Donation details submitted' : 'Unable to submit details'}</Modal.Title></Modal.Header>
          <Modal.Body className="text-center">
            {successMessage ? (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                <h4 className="text-success mb-2">Success!</h4>
                <p>{successMessage}</p>
              </div>
            ) : errorMessage ? (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
                <h4 className="text-danger mb-2">Error!</h4>
                <p>{errorMessage}</p>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => { setShowMessageModal(false); setSuccessMessage(null); setErrorMessage(null); }}>Close</Button></Modal.Footer>
        </Modal>
      </section>
    </div>
  );
};

export default Donate;
